import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FlexGrid, Loader, Input, Notification } from '@gasbuddy/react-components';

import Filter from './filter';
import Button from '../button';
import { SortOrder, ReviewSentiment, ReviewType } from '../../../lib/constants/enums';
import ReviewCard from '../reputation-tab/review-card';
import ExportReviewsModal from './export-reviews-modal';

import styles from './styles.css';

const { canExportPDF } = require('../../../lib/permissions').default;


const { api, getQueryParameters, setQueryParameter } = require('../../../lib/bputils').default;


const normalizeOption = option => option
  .split('_')
  .map(opt => opt.charAt(0).toUpperCase() + opt.slice(1))
  .join(' ');

const sortOrderOptions = Object.keys(SortOrder).map(sortOption => ({
  label: normalizeOption(sortOption),
  value: sortOption,
}));

const sortOrderFilters = {
  name: 'Date Submitted',
  type: 'radio',
  values: sortOrderOptions,
};

const reviewTypeOptions = Object.values(ReviewType).map(sortOption => ({
  label: normalizeOption(sortOption),
  value: sortOption,
}));

const reviewTypeFilters = {
  name: 'Review Type',
  type: 'checkbox',
  values: reviewTypeOptions,
};

const sentimentOptions = Object.keys(ReviewSentiment).map(sortOption => ({
  label: normalizeOption(sortOption),
  value: sortOption,
}));

const sentimentFilters = {
  name: 'Review Sentiment',
  type: 'radio',
  values: sentimentOptions,
};

const REVIEW_FETCH_LIMIT = 100;

const MINIMUM_REVIEW_CARD_HEIGHT = 228 + 15; // card had a height of 228px and 15px are spacing between each review.

export default function ReviewsList({ stationListId, startDate, endDate }) {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState();
  const [totalReviewCount, setTotalReviewCount] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest');
  const [reviewSentiment, setReviewSentiment] = useState(null);
  const [reviewTypes, setReviewTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [brandsMap, setBrandsMap] = useState();
  const [reviewOffset, setReviewOffset] = useState(0);
  const [selectedReviewId, setSelectedReviewId] = useState();
  const [showExportModal, setShowExportModal] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [requestingMoreReviews, setRequestingMoreReviews] = useState(false);
  const reviewsListRef = useRef();

  useEffect(() => {
    const queryParam = getQueryParameters().reviewId;
    if (queryParam) {
      setSelectedReviewId(Number(queryParam));
    }
  }, []);

  useEffect(() => {
    getReviews();
  }, [reviewSentiment, reviewTypes]);

  useEffect(() => {
    if (initialFetchDone) {
      scrollToReview();
    }
  }, [initialFetchDone]);

  useEffect(() => {
    getReviews();
  }, [sortOrder]);

  useEffect(() => {
    getBrands();
  }, []);

  useEffect(() => {
    if (stationListId && startDate && endDate) {
      getReviews();
    }
  }, [stationListId, startDate, endDate]);

  useEffect(() => {
    if (selectedReviewId) {
      setQueryParameter('reviewId', selectedReviewId);
    }
  }, [selectedReviewId]);

  const scrollToReview = () => {
    if (!selectedReviewId) {
      return;
    }
    try {
      const reviewElement = document.querySelector(`div[data-review-id='${selectedReviewId}']`);
      if (reviewElement) {
        reviewElement.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (err) {
      // do not throw an error
    }
  };

  const getBrands = () => {
    api.get({
      path: '/brands',
      requestId: 'reviewsListBrands',
      error: () => {
        Notification.error('Unable to load brands');
      },
      success: (data) => {
        setBrandsMap(Object.assign({}, ...data.map(b => ({ [b.id]: b }))));
      },
    });
  };

  const getReviewPostBody = offset => ({
    filters: [...reviewTypes, ...(reviewSentiment ? [reviewSentiment] : [])],
    limit: REVIEW_FETCH_LIMIT,
    offset,
    search_term: searchTerm,
    sort_direction: sortOrder === SortOrder.newest ? 'desc' : 'asc',
    sort_field: 'create_date',
  });

  const getReviews = (offset = 0) => {
    const shouldResetReviews = offset === 0;
    if (shouldResetReviews) {
      setLoading(true);
      setTotalReviewCount(null);
      setReviewOffset(0);
    }

    api.post({
      requestId: 'getReviewsByStationList',
      path: `/station-lists/${stationListId}/reviews?start_date=${startDate}&end_date=${endDate}`,
      data: getReviewPostBody(offset),
      success: (res) => {
        if (shouldResetReviews) {
          setReviews(res.reviews);
          setTotalReviewCount(res.meta.fetched_reviews_count);
          setLoading(false);
        } else {
          setReviews([...reviews, ...res.reviews]);
          setRequestingMoreReviews(false);
        }
        if (!initialFetchDone) {
          setInitialFetchDone(true);
        }
      },
      error: () => {
        Notification.error('There was an error fetching your reviews');
        setLoading(false);
      },
    });
  };

  const handleOnFilterChange = (values) => {
    const selectedReviewSentiment = values.filter(val => Object.values(ReviewSentiment).includes(val));
    const selectedReviewTypes = values.filter(val => !selectedReviewSentiment.includes(val));
    const rSentiment = selectedReviewSentiment.length ? selectedReviewSentiment[0] : null;
    if (rSentiment !== reviewSentiment) {
      setReviewSentiment(rSentiment);
    }
    if (selectedReviewTypes.sort().join(',') !== reviewTypes.sort().join(',')) {
      setReviewTypes(selectedReviewTypes);
    }
  };

  const handleOnSearchTermChange = ({ target: { value } }) => {
    setSearchTerm(value);
  };

  const handleOnKeyDown = (e) => {
    if (e.key === 'Enter') {
      getReviews();
    }
  };

  const handleOnReviewListScroll = (e) => {
    const nextReviewOffset = reviewOffset + REVIEW_FETCH_LIMIT;
    if (totalReviewCount === reviews.length || nextReviewOffset > totalReviewCount) {
      return;
    }

    const {
      scrollHeight,
      scrollTop: currentScrollY,
      offsetHeight: scrollableContainerHeight,
    } = e.target;

    // if we've scolled into view of the last 25 reviews and there's more to fetch we should fetch them
    const reachedEndOfPage = (currentScrollY + scrollableContainerHeight) > (scrollHeight - (MINIMUM_REVIEW_CARD_HEIGHT * 25));
    if (!requestingMoreReviews && reachedEndOfPage) {
      setRequestingMoreReviews(true);
      setReviewOffset(nextReviewOffset);
      getReviews(nextReviewOffset);
    }
  };

  const handleOnSortOrderChange = (value) => {
    setSortOrder(value[0]);
  };

  const handleExportData = useCallback(() => {
    setShowExportModal(true);
  }, []);

  const handleReviewCardClick = useCallback((reviewId) => {
    setSelectedReviewId(reviewId);
  }, []);

  const handleSearch = useCallback(() => {
    getReviews();
  }, []);

  const handleCloseExportDataModal = useCallback(() => {
    setShowExportModal(false);
  }, []);

  return (
    <Fragment>
      <FlexGrid container className={styles.reviewsListContainer}>
        <FlexGrid.Column className={styles.listHeader}>
          <div className={`${styles.flexRow} ${styles.flexSpaceBetween}`}>
            <div className={styles.reviewsListTitle}>
              <h4>Reviews</h4>
              {totalReviewCount ? ` (${totalReviewCount})` : ''}
            </div>
            <div>
              {canExportPDF() && (
                <Button primary size="sm" onClick={handleExportData}>
                  Export data
                </Button>
              )}
            </div>
          </div>
          <div className={`${styles.flexRow} ${styles.reviewsListFilters}`}>
            <Filter
              name="Filter"
              selected={[...reviewTypes, ...(reviewSentiment ? [reviewSentiment] : [])]}
              onChange={handleOnFilterChange}
              filters={[reviewTypeFilters, sentimentFilters]}
              queryParamId="filterBy"
            />
            <Filter
              name="Sort"
              selected={[sortOrder]}
              onChange={handleOnSortOrderChange}
              filters={[sortOrderFilters]}
              queryParamId="sortBy"
            />
          </div>
          <div className={styles.flexRow}>
            <Input
              value={searchTerm}
              onChange={handleOnSearchTermChange}
              placeholder="Search by keyword"
              className={styles.reviewKeywordSearch}
              onKeyDown={handleOnKeyDown}
            />
            <Button onClick={handleSearch} secondary>
              Search
            </Button>
          </div>
        </FlexGrid.Column>
        <FlexGrid.Column>
          {!!loading && (
            <div className={styles.loadingContainer}>
              <Loader size="lg" />
            </div>
          )}
          {(!loading && reviews && !reviews.length) && (
            <div className={styles.noReviews}>
              {reviewTypes?.length ? (
                <div>
                  <div className={styles.noReviewsTitle}>No reviews found with applied filters</div>
                  <div>Please try updating filters and/or date range.</div>
                </div>
              ) : (
                <div>
                  <div className={styles.noReviewsTitle}>Your List Is Empty</div>
                  <div>It looks like you have no current feedback to review.</div>
                </div>
              )}
            </div>
          )}
          <div
            className={styles.reviewsList}
            ref={reviewsListRef}
            onScroll={handleOnReviewListScroll}
          >
            {(!loading && !!reviews?.length && brandsMap) && (
              reviews.map(review => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  brandsMap={brandsMap}
                  className={`${styles.review} ${review.id === selectedReviewId ? styles.selectedReview : ''}`}
                  onClick={handleReviewCardClick}
                  shareable
                />
              ))
            )}
            {requestingMoreReviews && (
              <div style={{ padding: '32px 0' }}>
                <Loader />
                <div style={{ textAlign: 'center' }}>
                  Fetching more reviews
                </div>
              </div>
            )}
          </div>
        </FlexGrid.Column>
      </FlexGrid>
      <ExportReviewsModal
        show={showExportModal}
        stationListId={stationListId}
        startDate={startDate}
        endDate={endDate}
        onClose={handleCloseExportDataModal}
        searchTerm={searchTerm}
        filters={[...reviewTypes, ...(reviewSentiment ? [reviewSentiment] : [])]}
        sortOrder={sortOrder === SortOrder.newest ? 'desc' : 'asc'}
      />
    </Fragment>
  );
}

ReviewsList.propTypes = {
  stationListId: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
};
